import { type ClassValue, clsx } from 'clsx'
import { twMerge } from 'tailwind-merge'
import {
  MS_PER_MINUTE,
  MINUTES_PER_HOUR,
  HOURS_PER_DAY,
  BYTES_PER_KB,
  BYTES_PER_MB,
} from './constants'

export function cn(...inputs: ClassValue[]): string {
  return twMerge(clsx(inputs))
}

/**
 * Parse a date string as UTC.
 *
 * Handles timestamps from the API which may be in format:
 * - "2024-01-15 14:30:00" (SQLite datetime without timezone - interpret as UTC)
 * - "2024-01-15T14:30:00Z" (ISO 8601 with Z suffix)
 * - "2024-01-15T14:30:00.000Z" (ISO 8601 with milliseconds)
 *
 * If the string doesn't have a timezone indicator, we append 'Z' to interpret as UTC.
 */
export const parseUTCDate = (value: string | Date | null | undefined): Date | null => {
  if (!value) return null
  if (value instanceof Date) return value

  // If the string doesn't contain timezone info (Z, +, or - offset), treat as UTC
  // SQLite datetime format: "2024-01-15 14:30:00"
  // ISO format with Z: "2024-01-15T14:30:00Z" or "2024-01-15T14:30:00.000Z"
  let dateStr = value.trim()

  // Check if it has timezone info
  const hasTimezone = dateStr.endsWith('Z') ||
                      /[+-]\d{2}:\d{2}$/.test(dateStr) ||
                      /[+-]\d{4}$/.test(dateStr)

  if (!hasTimezone) {
    // Replace space with T for ISO format and append Z for UTC
    dateStr = dateStr.replace(' ', 'T') + 'Z'
  }

  return new Date(dateStr)
}

export const formatDate = (value?: string | Date | null): string => {
  if (!value) return '—'
  const date = parseUTCDate(value)
  if (!date) return '—'
  return new Intl.DateTimeFormat('en', {
    month: 'short',
    day: 'numeric',
    year: 'numeric'
  }).format(date)
}

export const formatRelative = (value?: string | Date | null): string => {
  if (!value) return 'Just now'
  const date = parseUTCDate(value)
  if (!date) return 'Just now'
  const now = new Date()
  const diff = Math.max(0, now.getTime() - date.getTime())
  const minutes = Math.floor(diff / MS_PER_MINUTE)
  if (minutes < 1) return 'Just now'
  if (minutes < MINUTES_PER_HOUR) return `${minutes}m ago`
  const hours = Math.floor(minutes / MINUTES_PER_HOUR)
  if (hours < HOURS_PER_DAY) return `${hours}h ago`
  const days = Math.floor(hours / HOURS_PER_DAY)
  return `${days}d ago`
}

export const formatCurrency = (value: number, currency = 'USD'): string =>
  new Intl.NumberFormat('en', {
    style: 'currency',
    currency,
    minimumFractionDigits: Number.isInteger(value) ? 0 : 2,
    maximumFractionDigits: Number.isInteger(value) ? 0 : 2,
  }).format(value)

export const formatFileSize = (bytes?: number | null): string => {
  if (bytes === null || bytes === undefined) return '—'
  if (bytes < BYTES_PER_KB) return `${bytes} B`
  if (bytes < BYTES_PER_MB) return `${(bytes / BYTES_PER_KB).toFixed(1)} KB`
  return `${(bytes / BYTES_PER_MB).toFixed(1)} MB`
}

export const formatDimensions = (width?: number | null, height?: number | null): string => {
  if (!width || !height) return '—'
  return `${width} × ${height} px`
}

/**
 * Format a timestamp with date, time, and seconds precision
 * Example: "Jan 15, 2026, 2:45:23 PM"
 */
export const formatDateTime = (value?: string | Date | null): string => {
  if (!value) return '—'
  const date = parseUTCDate(value)
  if (!date) return '—'
  return new Intl.DateTimeFormat('en', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
    second: '2-digit',
    hour12: true
  }).format(date)
}

/**
 * Split a filename into name and extension parts
 * @param filename The full filename including extension
 * @returns Object with name (without extension) and extension (with dot)
 */
export const splitFilename = (filename: string): { name: string; extension: string } => {
  const lastDotIndex = filename.lastIndexOf('.')
  if (lastDotIndex === -1 || lastDotIndex === 0) {
    return { name: filename, extension: '' }
  }
  return {
    name: filename.slice(0, lastDotIndex),
    extension: filename.slice(lastDotIndex)
  }
}
